import { Button } from "@/components/ui/button";
import { CircleAlert, Minimize2, Plus } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import {
    primaryButtonClassName,
    secondaryButtonClassName,
} from "./assistantClasses";

interface ConversationFullCardProps {
    /** Step-based contract: the conversation is full (gauge at 100 %); v1: 8 exchanges reached. */
    hasConversationMemory: boolean;
    canCompact: boolean;
    isCompacting: boolean;
    onCompact: () => void;
    isResetting: boolean;
    onNewConversation: () => void;
}

/** In place of the input bar once the conversation can't go on: compact it, or start over. */
const ConversationFullCard = ({
    hasConversationMemory,
    canCompact,
    isCompacting,
    onCompact,
    isResetting,
    onNewConversation,
}: ConversationFullCardProps) => {
    const { t } = useTranslation();
    const titleId = useId();

    return (
        <section
            aria-labelledby={hasConversationMemory ? titleId : undefined}
            className="flex w-full flex-col gap-3 rounded-2xl border border-assistant-cardBorder bg-white p-4"
        >
            {hasConversationMemory && (
                <div className="flex items-center gap-2.5">
                    <CircleAlert
                        aria-hidden
                        className="h-4 w-4 flex-none text-error-dark"
                    />
                    <h2
                        className="text-base font-semibold text-primary-darkest"
                        id={titleId}
                    >
                        {t("vistassistant.conversationFullTitle")}
                    </h2>
                </div>
            )}
            <p className="text-sm leading-relaxed text-assistant-muted">
                {hasConversationMemory
                    ? t("vistassistant.conversationFull")
                    : t("vistassistant.conversationLimitReached")}
            </p>
            <div className="flex flex-wrap gap-2">
                {canCompact && (
                    <Button
                        className={primaryButtonClassName}
                        disabled={isCompacting}
                        onClick={onCompact}
                        type="button"
                    >
                        <Minimize2 />
                        {t("vistassistant.compactConversationLong")}
                    </Button>
                )}
                <Button
                    aria-label={t("vistassistant.newConversation")}
                    className={secondaryButtonClassName}
                    disabled={isResetting}
                    onClick={onNewConversation}
                    type="button"
                    variant="outline"
                >
                    <Plus />
                    {t("vistassistant.newConversation")}
                </Button>
            </div>
        </section>
    );
};

export default ConversationFullCard;
